import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { CardBody } from "@/components/ui/card"
import { CommentSection } from "@/modules/patients/detail/progress-note/administrative-progress-note-card"
import { AttachmentsSection } from "@/modules/patients/detail/progress-note/attachments"
import { ProgressNoteReadOnlyCardSection } from "@/modules/patients/detail/progress-note/components/progress-note-readonly-card-section"
import { Questionnaire } from "@/modules/patients/detail/progress-note/components/questionnaire"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { QuestionnaireCriticalitySummaryFragment$key } from "@/modules/patients/services/__generated__/QuestionnaireCriticalitySummaryFragment.graphql"
import { QuestionnaireCriticalitySummaryFragment } from "@/modules/patients/services/QuestionnaireCriticalitySummaryFragment"

export const AdministrativeProgressNoteEditableCardBody = ({
  episodeOfCareProgressNoteData,
}: {
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
}): React.JSX.Element => {
  const { t } = useTranslation()
  const setProgressNoteCreatedDate = useReferralStoreContext((state) => state.setProgressNoteCreatedDate)
  const {
    appointment: {
      reason,
      appointmentAttachments,
      id,
      telephoneLine,
      questionnaireId,
      questionnaireCriticalitySummary,
    },
    createdAt,
  } = episodeOfCareProgressNoteData

  const criticalitySummary = useFragment<QuestionnaireCriticalitySummaryFragment$key>(
    QuestionnaireCriticalitySummaryFragment,
    questionnaireCriticalitySummary,
  )

  useEffect(() => {
    setProgressNoteCreatedDate(new Date(createdAt))
  }, [createdAt, setProgressNoteCreatedDate])

  return (
    <CardBody>
      <ProgressNoteReadOnlyCardSection
        isEditMode={true}
        title={t("episodeOfCare.progressNote.reasonForEncounter")}
        testId="progress-note-reason">
        <div className="grid gap-4">
          <span>{reason}</span>
          {questionnaireId && (
            <Questionnaire
              episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
              criticalitySummary={criticalitySummary}
            />
          )}
          <AttachmentsSection
            appointmentAttachments={appointmentAttachments}
            telephoneLine={telephoneLine}
            appointmentId={id}
            isReadOnlyProgressNote
          />
        </div>
      </ProgressNoteReadOnlyCardSection>
      <CommentSection episodeOfCareProgressNoteData={episodeOfCareProgressNoteData} />
    </CardBody>
  )
}
